import { toast } from "react-toastify";

import firestore from "src/apiService/firestore";
import { getUserId } from "src/config/storage";
import {
  userPlanSelector,
  effectiveUserTimeZoneSelector,
  isImpersonationSessionSelector,
  isSupportUserSessionSelector,
  userGetInventoryTypeSelector,
  featureFlagSelector,
} from "src/store/system/selector";
import { getVintedIntegrations } from "src/store/plaidIntegration/selector";
import type { VintedIntegration } from "src/interfaces/plaidIntegration.interface";
import type MonthYear from "src/interfaces/monthYear.interface";
import {
  endOfMonthInTimeZone,
  startOfMonthInTimeZone,
} from "src/utils/timezone";
import { eachMonthInclusive } from "src/utils/monthYear";
import {
  getVintedUser,
  RequiredExtensionVersion,
} from "src/utils/extension/vinted";
import { vintedIntegrationCreate } from "src/apiService/modules/vinted";

import type { Dispatcher, ResumeSyncStatus, SyncStatus } from "../../types";
import { updateSyncStatus as _updateSyncStatus } from "../actions";
import {
  saveSyncDate,
  getLastSyncDate,
  setBeforeUnloadHandler,
  clearBeforeUnloadHandler,
  ReSyncInterval,
  RunningKeys,
  isUserAllowedToDirectImport,
  shouldRunDailySync,
  checkIfItIsInitialImport,
  checkExtensionOrDispatchError,
  notifySyncAlreadyLocked,
} from "../utils";
import {
  withSyncLock,
  claimExpiredLease,
  releaseSyncLeaseIfOwner,
  VintedSyncLockTtlMs,
  type SyncLock,
} from "../syncLock";

import VintedClient, {
  NotLoggedError,
  extractVintedItemPagePayloads,
} from "./VintedClient";
import { enrichInventoryItemsWithPageDetails } from "./vintedInventoryEnrich";
import {
  createVintedSyncRunId,
  recordVintedSyncAborted,
  recordVintedSyncFinished,
  recordVintedSyncTriggered,
} from "./vintedSyncEvents";
import {
  clearVintedInventoryResume,
  clearVintedSalesResume,
  collectMrgSaleTransactionIdsForDateRange,
  findInventoryResumeCursor,
  findOrdersResumeCursor,
  monthsForSalesRange,
  runInventoryImportPages,
  runOrdersImportPages,
  saveVintedInventoryResume,
  saveVintedSalesResume,
  walletResumeStartMonthIndex,
} from "./vintedDirectImport";
import { discardVintedResume } from "./vintedResumeOffer";
import { salesExistFlagsForPage } from "./resumeCutPoint";
import {
  getExistingVintedInventoryListingIds,
  loadPendingVintedInventoryListingIds,
  writeVintedInventoryDownloadBatch,
  VintedInventoryDailyPageSize,
} from "./vintedInventoryDaily";
import {
  addVintedThreadIdsToSet,
  buildThreadIdAliasMap,
  getInventoryListingId,
  getMrgSaleTransactionId,
  getVintedOrderApiId,
  uploadSalesBatch,
} from "./vintedBatches";
import { syncTransactionsForMonths } from "./vintedWallet";

function getOrderDate(order: Record<string, unknown>): Date | null {
  // Vinted's my_orders rows carry the sold timestamp on `date`. This runs
  // during filtering, before enrichment, so only top-level fields exist here.
  const raw = order.date;
  if (!raw) return null;
  const d = new Date(String(raw));
  return Number.isNaN(d.getTime()) ? null : d;
}

function getInventoryItemDate(item: Record<string, unknown>): Date | null {
  const photos = item.photos as
    | Array<{ is_main?: boolean; high_resolution?: { timestamp?: number } }>
    | undefined;
  const main = photos?.find((photo) => photo.is_main) || photos?.[0];
  const ts = main?.high_resolution?.timestamp;
  if (ts == null) return null;
  return new Date(Number(ts) * 1000);
}

function getSoldItemUrl(details: unknown): string | null {
  const items = (
    details as {
      transaction?: { order?: { items?: Array<{ url?: string }> } };
    }
  )?.transaction?.order?.items;
  const url = Array.isArray(items) ? items[0]?.url : undefined;
  return typeof url === "string" && url ? url : null;
}

async function enrichOrdersWithTransactionDetails(
  client: VintedClient,
  orders: Record<string, unknown>[]
) {
  const results: Record<string, unknown>[] = [];

  for (const order of orders) {
    const apiOrderId = getVintedOrderApiId(order);
    const transactionId = getMrgSaleTransactionId(order);
    if (!apiOrderId) {
      results.push({ ...order });
      continue;
    }

    // Don't let one failed order-detail fetch abort the whole import; keep the
    // order (backend can still fall back to the my_orders summary).
    let details: unknown = null;
    try {
      details = await client.getTransactionDetail(apiOrderId);
    } catch (e) {
      if (e instanceof NotLoggedError) throw e;
      console.warn("[Vinted] Failed to fetch transaction detail", {
        apiOrderId,
        transactionId,
        error: (e as Error).message,
      });
    }

    // Fetch the sold item's listing page for category/brand taxonomy — the same
    // enrichment inventory relies on. A closed listing may 404, so tolerate it.
    let itemPage: ReturnType<typeof extractVintedItemPagePayloads> | undefined;
    const itemUrl = getSoldItemUrl(details);
    if (itemUrl) {
      try {
        const html = await client.getItemPage(itemUrl);
        itemPage = extractVintedItemPagePayloads(html);
      } catch (e) {
        if (e instanceof NotLoggedError) throw e;
        console.warn("[Vinted] Failed to fetch sold item page", {
          apiOrderId,
          transactionId,
          itemUrl,
          error: (e as Error).message,
        });
      }
    }

    results.push({ ...order, details, itemPage });
  }

  return results;
}

async function updateIntegration(integrationId: string) {
  try {
    const data = await getVintedUser();

    if (data.cookies && data.userId) {
      await vintedIntegrationCreate({
        integrationId,
        cookies: data.cookies,
        username: data.username || "",
        userId: String(data.userId),
      });
    }
  } catch (err) {
    console.error("There was an error:", err);
  }
}

function updateSyncStatus(status: SyncStatus) {
  return _updateSyncStatus({
    integration: "vinted" as const,
    status,
  });
}

function dispatchNotLoggedInError(
  dispatch: (action: ReturnType<typeof updateSyncStatus>) => void,
  e: unknown,
  dispatcher: Dispatcher
): e is NotLoggedError {
  if (!(e instanceof NotLoggedError)) return false;
  console.warn(e);
  dispatch(
    updateSyncStatus({
      type: "error",
      message: e.message,
      code: "user-not-logged-in",
      dispatcher,
    })
  );
  return true;
}

async function verifyLoggedInUser(
  client: VintedClient,
  integration: VintedIntegration
) {
  const cookieUserId = await client.getUserIdFromCookie();
  if (String(cookieUserId) !== String(integration.uid)) {
    throw new NotLoggedError();
  }
  // Homepage 200 (after optional session-refresh popup), then a real API call.
  await client.refreshSession();
  await client.assertAuthenticatedSession();
}

async function syncInProcess(_client: VintedClient) {
  const uid = await getUserId();
  const db = firestore();
  const snapshot = await db
    .collection("Sales")
    .where("user", "==", uid)
    .where("in_process", "==", true)
    .where("import_platform", "==", "vinted")
    .get();

  console.log(
    `[Vinted] syncInProcess stub: ${snapshot.size} in-process sales pending order-detail endpoint`
  );
}

async function syncSalesDaily(
  client: VintedClient,
  integration: VintedIntegration,
  date: Date,
  lock: SyncLock,
  knownSaleTransactionIds: Set<string>,
  threadIdByAnyId: Map<string, string[]>
) {
  const lastDateStr = getLastSyncDate(integration, "vintedLastSyncDateSales");
  const lastDate = new Date(lastDateStr);
  const timestamp = date.valueOf();
  let batchIndex = 0;
  // Cross-page dedup for every order we see (including Firestore skips and
  // date-cutoff rows). Separate from knownSaleTransactionIds, which only
  // holds ids we upload this run — Sold wallet filtering must not omit lines
  // for sales that already existed (those still need posted_date stamps).
  const seenOrderIds = new Set<string>();

  for await (const orders of client.getMyOrdersFull()) {
    lock.throwIfAborted();
    let shouldStop = false;
    const candidates: Record<string, unknown>[] = [];

    for (const order of orders) {
      const transactionId = getMrgSaleTransactionId(order);
      if (transactionId) {
        if (seenOrderIds.has(transactionId)) continue;
        seenOrderIds.add(transactionId);
      }

      const orderDate = getOrderDate(order as Record<string, unknown>);
      if (!orderDate) {
        candidates.push(order as Record<string, unknown>);
        continue;
      }
      if (orderDate < lastDate) {
        shouldStop = true;
        continue;
      }
      candidates.push(order as Record<string, unknown>);
    }

    // Skip enrich+upload when a Vinted sale is already booked (any import).
    // Resolve UUID → canonical so list rows that only expose UUID still match
    // sales stored under num11/`500*`.
    const existFlags = await salesExistFlagsForPage(
      candidates,
      undefined,
      async (threadId) => {
        lock.throwIfAborted();
        const res = await client.getConversation(threadId);
        const id = (res as { conversation?: { id?: unknown } })?.conversation
          ?.id;
        return id == null || id === "" ? null : String(id);
      }
    );
    const toUpload = candidates.filter((_, i) => !existFlags[i]);

    const detailedOrders = await enrichOrdersWithTransactionDetails(
      client,
      toUpload
    );

    for (const order of detailedOrders) {
      addVintedThreadIdsToSet(knownSaleTransactionIds, order);
    }
    for (const [id, aliases] of buildThreadIdAliasMap(detailedOrders)) {
      const existing = threadIdByAnyId.get(id) || [];
      for (const alias of aliases) {
        if (!existing.includes(alias)) existing.push(alias);
      }
      threadIdByAnyId.set(id, existing);
    }

    if (detailedOrders.length) {
      await uploadSalesBatch(
        integration,
        detailedOrders,
        "daily-sync",
        {
          timestamp,
          batchIndex: batchIndex++,
        },
        lock
      );
    }

    if (shouldStop) break;
  }
}

async function syncInventoryDaily(
  client: VintedClient,
  integration: VintedIntegration,
  date: Date,
  lock: SyncLock,
  state: unknown,
  pendingInventoryEnabled: boolean,
  syncRunId: string,
  trigger: string
) {
  const timestamp = date.valueOf();
  const lastDateStr = getLastSyncDate(
    integration,
    "vintedLastSyncDateInventory"
  );
  const lastDate = new Date(lastDateStr);

  const skipListingIds = getExistingVintedInventoryListingIds(state);
  const pendingIds = await loadPendingVintedInventoryListingIds({
    integrationId: integration.id,
    lastSyncDate: lastDate,
    pendingInventoryEnabled,
  });
  for (const id of pendingIds) skipListingIds.add(id);

  const seenListingIds = new Set<string>();
  let batchIndex = 0;
  let page = 0;

  for await (const items of client.getWardrobeItemsFull(integration.uid, {
    perPage: VintedInventoryDailyPageSize,
  })) {
    page += 1;
    lock.throwIfAborted();
    let shouldStop = false;
    const pageItems: Record<string, unknown>[] = [];

    for (const item of items) {
      const listingId = getInventoryListingId(item);
      if (listingId) {
        if (seenListingIds.has(listingId)) continue;
        seenListingIds.add(listingId);
        // Already in Inventory or in a post-cursor pending Downloads batch.
        if (skipListingIds.has(listingId)) continue;
      }

      const itemDate = getInventoryItemDate(item as Record<string, unknown>);
      if (itemDate && itemDate < lastDate) {
        shouldStop = true;
        continue;
      }
      pageItems.push(item as Record<string, unknown>);
    }

    // One Downloads blob per wardrobe page that has items left to review.
    if (pageItems.length) {
      const detailedItems = await enrichInventoryItemsWithPageDetails(
        client,
        pageItems,
        {
          syncRunId,
          job: "daily-sync",
          importType: "daily-sync",
          trigger,
          page,
        }
      );
      for (const item of detailedItems) {
        const id = getInventoryListingId(item);
        if (id) skipListingIds.add(id);
      }
      await writeVintedInventoryDownloadBatch({
        integration,
        items: detailedItems,
        date,
        timestamp,
        batchIndex: batchIndex++,
        lock,
        pendingInventoryEnabled,
      });
    }

    if (shouldStop) break;
  }
}

function syncDaily(
  integrationId: string,
  inventory: boolean,
  dispatcher: Dispatcher,
  syncRunId: string
) {
  return async (
    dispatch,
    getState
  ): Promise<{ started: boolean; aborted: boolean }> => {
    let aborted = false;
    const getIntegration = (): VintedIntegration => {
      const integration = getVintedIntegrations(getState()).find(
        ({ id }) => id === integrationId
      );
      if (!integration) throw Error(`Integration not found ${integrationId}`);
      return integration;
    };

    const started = await withSyncLock(
      integrationId,
      RunningKeys,
      async (lock) => {
        const db = firestore();
        const start = new Date();

        const client = new VintedClient();
        await verifyLoggedInUser(client, getIntegration());
        await updateIntegration(integrationId);

        dispatch(updateSyncStatus({ type: "syncing", step: "sales" }));
        const knownSaleTransactionIds = new Set<string>();
        const threadIdByAnyId = new Map<string, string[]>();
        await syncSalesDaily(
          client,
          getIntegration(),
          start,
          lock,
          knownSaleTransactionIds,
          threadIdByAnyId
        );
        await syncInProcess(client);

        lock.throwIfAborted();
        await saveSyncDate(
          integrationId,
          "vintedLastSyncDateSales",
          start.toISOString()
        );

        dispatch(updateSyncStatus({ type: "syncing", step: "transactions" }));
        const lastTxnDateStr = getLastSyncDate(
          getIntegration(),
          "vintedLastSyncDateTransactions"
        );
        const lastTxnDate = new Date(lastTxnDateStr);
        await syncTransactionsForMonths(
          client,
          getIntegration(),
          eachMonthInclusive(
            {
              year: lastTxnDate.getFullYear(),
              month: lastTxnDate.getMonth() + 1,
            },
            { year: start.getFullYear(), month: start.getMonth() + 1 }
          ),
          "daily-sync",
          {
            timestamp: start.valueOf(),
            knownSaleTransactionIds,
            threadIdByAnyId,
          },
          lock
        );
        lock.throwIfAborted();
        await saveSyncDate(
          integrationId,
          "vintedLastSyncDateTransactions",
          start.toISOString()
        );

        if (inventory) {
          dispatch(updateSyncStatus({ type: "syncing", step: "inventory" }));
          const pendingInventoryEnabled = !!featureFlagSelector(
            getState(),
            "pendingInventory"
          );
          await syncInventoryDaily(
            client,
            getIntegration(),
            start,
            lock,
            getState(),
            pendingInventoryEnabled,
            syncRunId,
            dispatcher
          );
          lock.throwIfAborted();
          await saveSyncDate(
            integrationId,
            "vintedLastSyncDateInventory",
            start.toISOString()
          );
        }

        lock.throwIfAborted();
        await db.collection("Plaid_Integrations").doc(integrationId).update({
          lastSync: start.toISOString(),
        });
        await updateIntegration(integrationId);
      },
      {
        ttlMs: VintedSyncLockTtlMs,
        onLostLock: () => {
          aborted = true;
          const integration =
            getVintedIntegrations(getState()).find(
              ({ id }) => id === integrationId
            ) || ({ id: integrationId } as VintedIntegration);
          recordVintedSyncAborted(integration, {
            job: "daily-sync",
            syncRunId,
            trigger: dispatcher,
            importType: "daily-sync",
            reason: "lost-lock",
            inventoryEnabled: inventory,
          });
        },
      }
    );

    if (!started) {
      notifySyncAlreadyLocked(dispatch, updateSyncStatus, "Vinted");
      const integration =
        getVintedIntegrations(getState()).find(
          ({ id }) => id === integrationId
        ) || ({ id: integrationId } as VintedIntegration);
      recordVintedSyncAborted(integration, {
        job: "daily-sync",
        syncRunId,
        trigger: dispatcher,
        importType: "daily-sync",
        reason: "lock-blocked",
        inventoryEnabled: inventory,
      });
    }
    return { started, aborted };
  };
}

export function vintedSync(
  integration: VintedIntegration,
  dispatcher?: Dispatcher
) {
  return async (dispatch, getState) => {
    const trigger = dispatcher ?? "auto";
    const syncRunId = createVintedSyncRunId();
    const extensionId = checkExtensionOrDispatchError(
      dispatch,
      updateSyncStatus,
      "Vinted",
      trigger,
      RequiredExtensionVersion
    );
    if (!extensionId) {
      recordVintedSyncAborted(integration, {
        job: "daily-sync",
        syncRunId,
        trigger,
        importType: "daily-sync",
        reason: "extension",
        message: "Vinted daily sync aborted (extension missing or outdated)",
      });
      return;
    }

    setBeforeUnloadHandler("Vinted");
    const startedAt = Date.now();

    try {
      const inventory =
        integration.inventory !== false &&
        userGetInventoryTypeSelector(getState()) !== "cash";
      recordVintedSyncTriggered(integration, {
        job: "daily-sync",
        syncRunId,
        trigger,
        importType: "daily-sync",
        inventoryEnabled: inventory,
      });
      const { started, aborted } = await dispatch(
        syncDaily(integration.id, inventory, trigger, syncRunId)
      );
      if (started && !aborted) {
        recordVintedSyncFinished(integration, {
          job: "daily-sync",
          syncRunId,
          trigger,
          importType: "daily-sync",
          inventoryEnabled: inventory,
          durationMs: Date.now() - startedAt,
        });
      }
      if (started) dispatch(updateSyncStatus(undefined));
    } catch (e) {
      if (e instanceof NotLoggedError) {
        recordVintedSyncAborted(integration, {
          job: "daily-sync",
          syncRunId,
          trigger,
          importType: "daily-sync",
          reason: "not-logged-in",
          error: e,
        });
      } else {
        recordVintedSyncAborted(integration, {
          job: "daily-sync",
          syncRunId,
          trigger,
          importType: "daily-sync",
          reason: "error",
          error: e,
        });
      }
      if (!dispatchNotLoggedInError(dispatch, e, trigger)) {
        console.error(e);
        dispatch(
          updateSyncStatus({
            type: "error",
            message: (e as Error).message || (e as Error).toString(),
            code: "unknown",
            dispatcher: trigger,
          })
        );
      }
    } finally {
      clearBeforeUnloadHandler("Vinted");
    }
  };
}

export function vintedSyncWithCheck(
  integrationId: string,
  noTimeCheck = false
) {
  return async (dispatch, getState) => {
    const state = getState();
    // Accountant / support impersonation sessions cannot trigger client-side syncs.
    if (isImpersonationSessionSelector(state)) return;
    const plan = userPlanSelector(state);

    if (plan === "ultimate") {
      const integration = getVintedIntegrations(state)?.find(
        ({ id }) => id === integrationId
      );

      if (
        integration &&
        integration.type === "vinted" &&
        integration.sync &&
        !integration.error
      ) {
        const shouldRun =
          noTimeCheck ||
          (!isSupportUserSessionSelector(state) &&
            (await shouldRunDailySync(integration.id, ReSyncInterval)));

        if (shouldRun) {
          await dispatch(
            vintedSync(integration, noTimeCheck ? "manual" : "auto")
          );
        }
      }
    }
  };
}

/**
 * Dismiss the abandoned direct-import offer. The banner is derived from the
 * integration doc, so this has to drop the checkpoint and the stale lease —
 * clearing only the status re-offers on the next render.
 */
export function vintedDismissResume(
  integrationId: string,
  job: ResumeSyncStatus["job"]
) {
  return async (dispatch) => {
    try {
      await discardVintedResume(integrationId, job);
      dispatch(updateSyncStatus(undefined));
    } catch (e) {
      // Checkpoint survived, so leave the banner up instead of hiding an offer
      // that would come straight back.
      console.error(e);
      toast.error(`Error dismissing the resume alert: ${(e as Error).message}`);
    }
  };
}

export function vintedSyncSales(
  integrationId: string,
  {
    start,
    end,
    resume = false,
  }: { start: MonthYear; end: MonthYear; resume?: boolean }
) {
  return async (
    dispatch,
    getState
  ): Promise<{ success?: boolean; error?: boolean }> => {
    const db = firestore();
    const state = getState();

    if (!isUserAllowedToDirectImport(state, "vintedSales")) {
      return { error: true };
    }

    const integration = getVintedIntegrations(state)?.find(
      ({ id }) => id === integrationId
    );

    if (!integration || integration.type !== "vinted" || integration.error) {
      return { error: true };
    }

    const existingResume = resume
      ? integration.vintedSalesImportResume
      : undefined;
    if (resume && !existingResume) {
      return { error: true };
    }

    const salesStart = existingResume?.start ?? start;
    const salesEnd = existingResume?.end ?? end;

    const extensionId = checkExtensionOrDispatchError(
      dispatch,
      updateSyncStatus,
      "Vinted",
      "manual",
      RequiredExtensionVersion
    );
    const syncRunId = createVintedSyncRunId();
    if (!extensionId) {
      recordVintedSyncAborted(integration, {
        job: "sales-import",
        syncRunId,
        trigger: "manual",
        reason: "extension",
        start: salesStart,
        end: salesEnd,
        resumed: resume || undefined,
        message: "Vinted sales import aborted (extension missing or outdated)",
      });
      return { error: true };
    }

    const client = new VintedClient();
    try {
      await verifyLoggedInUser(client, integration);
    } catch (e) {
      console.warn(e);
      recordVintedSyncAborted(integration, {
        job: "sales-import",
        syncRunId,
        trigger: "manual",
        reason: "not-logged-in",
        error: e,
        start: salesStart,
        end: salesEnd,
        resumed: resume || undefined,
      });
      dispatch(
        updateSyncStatus({
          type: "error",
          message: "You must be logged in to Vinted with the correct user",
          code: "user-not-logged-in",
          dispatcher: "manual",
        })
      );
      return { error: true };
    }
    await updateIntegration(integrationId);

    const runningKeys = ["salesIsRunning" as const];
    const isInitialImport = checkIfItIsInitialImport(state, "vintedSales");
    const importType =
      existingResume?.importType ??
      (isInitialImport ? "initial-import" : "direct-import");
    const tz = effectiveUserTimeZoneSelector(getState());
    const startDate = startOfMonthInTimeZone(
      salesStart.year,
      salesStart.month,
      tz
    );
    const endDate = endOfMonthInTimeZone(salesEnd.year, salesEnd.month, tz);

    setBeforeUnloadHandler("Vinted");
    let preserveLoginDialog = false;
    let lockBlocked = false;
    let aborted = false;
    const startedAt = Date.now();

    recordVintedSyncTriggered(integration, {
      job: "sales-import",
      syncRunId,
      trigger: "manual",
      importType,
      start: salesStart,
      end: salesEnd,
      resumed: resume || undefined,
    });

    let claimedOwner: string | undefined;
    if (resume) {
      // null ⇒ lease still fresh / doc missing — transaction wrote nothing.
      claimedOwner =
        (await claimExpiredLease(integrationId, runningKeys, {
          ttlMs: VintedSyncLockTtlMs,
        })) ?? undefined;
      if (!claimedOwner) {
        lockBlocked = true;
        notifySyncAlreadyLocked(dispatch, updateSyncStatus, "Vinted");
        recordVintedSyncAborted(integration, {
          job: "sales-import",
          syncRunId,
          trigger: "manual",
          importType,
          reason: "lock-blocked",
          start: salesStart,
          end: salesEnd,
          resumed: true,
        });
        return { error: true };
      }
    }

    try {
      const started = await withSyncLock(
        integrationId,
        runningKeys,
        async (lock) => {
          dispatch(
            updateSyncStatus({ type: "syncing", step: "direct-import-sales" })
          );

          const currentIntegration =
            getVintedIntegrations(getState()).find(
              ({ id }) => id === integrationId
            ) || integration;

          const uid = await getUserId();
          let timestamp = existingResume?.timestamp ?? Date.now();
          let uploadSalesId = existingResume?.uploadSalesId;
          let uploadReturnsId = existingResume?.uploadReturnsId;
          let uploadTransactionsId = existingResume?.uploadTransactionsId;
          let batchIndex = existingResume?.batchIndex ?? 0;
          let phase: "orders" | "transactions" =
            existingResume?.phase ?? "orders";

          if (!uploadSalesId || !uploadReturnsId) {
            timestamp = Date.now();
            const upload = db.collection("Uploads").doc();
            await upload.set({
              user: uid,
              type: "sales",
              provider: "vinted",
              filename: `${uid}/vintedSales/${timestamp}_${integrationId}.json`,
              n: 0,
              timestamp,
              id: upload.id,
            });
            const uploadReturns = db.collection("Uploads").doc();
            await uploadReturns.set({
              user: uid,
              type: "returns",
              provider: "vinted",
              filename: `${uid}/vintedSales/${timestamp}_${integrationId}.json`,
              n: 0,
              timestamp,
              id: uploadReturns.id,
            });
            uploadSalesId = upload.id;
            uploadReturnsId = uploadReturns.id;
            await saveVintedSalesResume(integrationId, {
              phase: "orders",
              page: 0,
              batchIndex: 0,
              timestamp,
              importType,
              uploadSalesId,
              uploadReturnsId,
              start: salesStart,
              end: salesEnd,
            });
          }

          let knownSaleTransactionIds: Set<string> | null = null;
          let threadIdByAnyId: Map<string, string[]> | null = null;
          // True when this process walked from page 1 through the date window.
          // startIndex only skips re-upload on the first page; ids are still
          // taken from each page's full in-range list, so the set is complete.
          let knownSaleIdsComplete = false;

          if (phase === "orders") {
            let startPage = 1;
            let startIndex = 0;
            if (existingResume?.phase === "orders" && existingResume.page > 0) {
              const cursor = await findOrdersResumeCursor(
                client,
                {
                  ...existingResume,
                  uploadSalesId,
                  uploadReturnsId,
                },
                startDate,
                endDate
              );
              if (cursor.done) {
                phase = "transactions";
              } else {
                startPage = cursor.page;
                startIndex = cursor.startIndex;
              }
            }

            if (phase === "orders") {
              const ordersResult = await runOrdersImportPages({
                client,
                integration: currentIntegration,
                startDate,
                endDate,
                start: salesStart,
                end: salesEnd,
                importType,
                timestamp,
                uploadSalesId,
                uploadReturnsId,
                batchIndex,
                startPage,
                startIndex,
                lock,
                enrichOrders: enrichOrdersWithTransactionDetails,
              });
              batchIndex = ordersResult.batchIndex;
              knownSaleTransactionIds = ordersResult.knownSaleTransactionIds;
              threadIdByAnyId = ordersResult.threadIdByAnyId;
              knownSaleIdsComplete = startPage === 1;
              phase = "transactions";
            }
          }

          await syncInProcess(client);

          dispatch(updateSyncStatus({ type: "syncing", step: "transactions" }));

          if (!uploadTransactionsId) {
            const uploadTransactions = db.collection("Uploads").doc();
            await uploadTransactions.set({
              user: uid,
              type: "transactions",
              provider: "vinted",
              filename: `${uid}/vintedTransactions/${timestamp}_${integrationId}.json`,
              n: 0,
              timestamp,
              id: uploadTransactions.id,
            });
            uploadTransactionsId = uploadTransactions.id;
            await saveVintedSalesResume(integrationId, {
              phase: "transactions",
              page: 0,
              batchIndex,
              timestamp,
              importType,
              uploadSalesId,
              uploadReturnsId,
              uploadTransactionsId,
              start: salesStart,
              end: salesEnd,
            });
          }

          // Upload docs are created above (or restored from resume).
          if (!uploadSalesId || !uploadReturnsId || !uploadTransactionsId) {
            throw new Error("Vinted sales import missing upload document ids");
          }
          const salesUploadId = uploadSalesId;
          const returnsUploadId = uploadReturnsId;
          const transactionsUploadId = uploadTransactionsId;

          const months = monthsForSalesRange(salesStart, salesEnd);
          const startAfterMonth =
            existingResume?.phase === "transactions" && resume
              ? existingResume.transactionMonth
              : undefined;
          const startMonthIndex = walletResumeStartMonthIndex(
            months,
            startAfterMonth
          );
          if (startMonthIndex >= months.length) {
            await clearVintedSalesResume(integrationId);
            return;
          }

          // Prefer ids accumulated during the orders walk. Only re-list my_orders
          // (no enrich) when this process did not start at page 1 — resume into
          // transactions, or resume mid-orders from a later page.
          const saleIdsForWallet =
            knownSaleIdsComplete && knownSaleTransactionIds
              ? knownSaleTransactionIds
              : await collectMrgSaleTransactionIdsForDateRange(
                  client,
                  startDate,
                  endDate,
                  lock
                );

          await syncTransactionsForMonths(
            client,
            currentIntegration,
            months.slice(startMonthIndex),
            importType,
            {
              timestamp,
              uploadTransactionsId: transactionsUploadId,
              batchIndex,
              knownSaleTransactionIds: saleIdsForWallet,
              threadIdByAnyId: threadIdByAnyId || undefined,
            },
            lock,
            async (month, nextBatchIndex) => {
              batchIndex = nextBatchIndex;
              await saveVintedSalesResume(integrationId, {
                phase: "transactions",
                page: 0,
                batchIndex: nextBatchIndex,
                timestamp,
                importType,
                uploadSalesId: salesUploadId,
                uploadReturnsId: returnsUploadId,
                uploadTransactionsId: transactionsUploadId,
                start: salesStart,
                end: salesEnd,
                transactionMonth: month,
              });
            }
          );

          await clearVintedSalesResume(integrationId);
        },
        {
          ttlMs: VintedSyncLockTtlMs,
          claimedOwner,
          onLostLock: () => {
            aborted = true;
            recordVintedSyncAborted(integration, {
              job: "sales-import",
              syncRunId,
              trigger: "manual",
              importType,
              reason: "lost-lock",
              start: salesStart,
              end: salesEnd,
              resumed: resume || undefined,
            });
          },
        }
      );
      if (!started) {
        lockBlocked = true;
        // Defensive: claimExpiredLease already stamped the lease; if we never
        // entered the lock body, release so we don't leave a fresh orphan.
        if (claimedOwner) {
          await releaseSyncLeaseIfOwner(
            integrationId,
            runningKeys,
            claimedOwner
          );
        }
        notifySyncAlreadyLocked(dispatch, updateSyncStatus, "Vinted");
        recordVintedSyncAborted(integration, {
          job: "sales-import",
          syncRunId,
          trigger: "manual",
          importType,
          reason: "lock-blocked",
          start: salesStart,
          end: salesEnd,
          resumed: resume || undefined,
        });
        return { error: true };
      }
      if (!aborted) {
        recordVintedSyncFinished(integration, {
          job: "sales-import",
          syncRunId,
          trigger: "manual",
          importType,
          durationMs: Date.now() - startedAt,
          start: salesStart,
          end: salesEnd,
          resumed: resume || undefined,
        });
      }
    } catch (e) {
      if (claimedOwner) {
        await releaseSyncLeaseIfOwner(integrationId, runningKeys, claimedOwner);
      }
      if (dispatchNotLoggedInError(dispatch, e, "manual")) {
        preserveLoginDialog = true;
        recordVintedSyncAborted(integration, {
          job: "sales-import",
          syncRunId,
          trigger: "manual",
          importType,
          reason: "not-logged-in",
          error: e,
          start: salesStart,
          end: salesEnd,
          resumed: resume || undefined,
        });
      } else {
        console.error(e);
        toast.error(`Error: ${(e as Error).message}`);
        recordVintedSyncAborted(integration, {
          job: "sales-import",
          syncRunId,
          trigger: "manual",
          importType,
          reason: "error",
          error: e,
          start: salesStart,
          end: salesEnd,
          resumed: resume || undefined,
        });
      }
      return { error: true };
    } finally {
      if (!lockBlocked && !preserveLoginDialog)
        dispatch(updateSyncStatus(undefined));
      clearBeforeUnloadHandler("Vinted");
    }

    await updateIntegration(integrationId);
    return { success: true };
  };
}

export function vintedSyncInventory(
  integrationId: string,
  { resume = false }: { resume?: boolean } = {}
) {
  return async (
    dispatch,
    getState
  ): Promise<{ error?: true; success?: true }> => {
    const db = firestore();
    const state = getState();

    if (!isUserAllowedToDirectImport(state, "vintedInventory")) {
      return { error: true };
    }
    if (userGetInventoryTypeSelector(getState()) === "cash") {
      return { error: true };
    }

    const integration = getVintedIntegrations(state)?.find(
      ({ id }) => id === integrationId
    );

    if (!integration || integration.type !== "vinted" || integration.error) {
      return { error: true };
    }

    const existingResume = resume
      ? integration.vintedInventoryImportResume
      : undefined;
    if (resume && !existingResume) {
      return { error: true };
    }

    const extensionId = checkExtensionOrDispatchError(
      dispatch,
      updateSyncStatus,
      "Vinted",
      "manual",
      RequiredExtensionVersion
    );
    const syncRunId = createVintedSyncRunId();
    if (!extensionId) {
      recordVintedSyncAborted(integration, {
        job: "inventory-import",
        syncRunId,
        trigger: "manual",
        reason: "extension",
        resumed: resume || undefined,
        message:
          "Vinted inventory import aborted (extension missing or outdated)",
      });
      return { error: true };
    }

    const client = new VintedClient();
    try {
      await verifyLoggedInUser(client, integration);
    } catch (e) {
      console.warn(e);
      recordVintedSyncAborted(integration, {
        job: "inventory-import",
        syncRunId,
        trigger: "manual",
        reason: "not-logged-in",
        error: e,
        resumed: resume || undefined,
      });
      dispatch(
        updateSyncStatus({
          type: "error",
          message: "You must be logged in to Vinted with the correct user",
          code: "user-not-logged-in",
          dispatcher: "manual",
        })
      );
      return { error: true };
    }
    await updateIntegration(integrationId);

    const runningKeys = ["inventoryIsRunning" as const];
    const isInitialImport = checkIfItIsInitialImport(state, "vintedInventory");
    const importType =
      existingResume?.importType ??
      (isInitialImport ? "initial-import" : "direct-import");
    setBeforeUnloadHandler("Vinted");
    let preserveLoginDialog = false;
    let lockBlocked = false;
    let aborted = false;
    const startedAt = Date.now();

    recordVintedSyncTriggered(integration, {
      job: "inventory-import",
      syncRunId,
      trigger: "manual",
      importType,
      resumed: resume || undefined,
    });

    let claimedOwner: string | undefined;
    if (resume) {
      // null ⇒ lease still fresh / doc missing — transaction wrote nothing.
      claimedOwner =
        (await claimExpiredLease(integrationId, runningKeys, {
          ttlMs: VintedSyncLockTtlMs,
        })) ?? undefined;
      if (!claimedOwner) {
        lockBlocked = true;
        notifySyncAlreadyLocked(dispatch, updateSyncStatus, "Vinted");
        recordVintedSyncAborted(integration, {
          job: "inventory-import",
          syncRunId,
          trigger: "manual",
          importType,
          reason: "lock-blocked",
          resumed: true,
        });
        return { error: true };
      }
    }

    try {
      const started = await withSyncLock(
        integrationId,
        runningKeys,
        async (lock) => {
          dispatch(
            updateSyncStatus({
              type: "syncing",
              step: "direct-import-inventory",
            })
          );

          const currentIntegration =
            getVintedIntegrations(getState()).find(
              ({ id }) => id === integrationId
            ) || integration;

          const uid = await getUserId();
          let timestamp = existingResume?.timestamp ?? Date.now();
          let uploadInventoryId = existingResume?.uploadInventoryId;
          let batchIndex = existingResume?.batchIndex ?? 0;

          if (!uploadInventoryId) {
            timestamp = Date.now();
            const upload = db.collection("Uploads").doc();
            await upload.set({
              user: uid,
              type: "inventory",
              provider: "vinted",
              filename: `${uid}/vintedInventory/${timestamp}_${integrationId}.json`,
              n: 0,
              timestamp,
              id: upload.id,
            });
            uploadInventoryId = upload.id;
            await saveVintedInventoryResume(integrationId, {
              page: 0,
              batchIndex: 0,
              timestamp,
              importType,
              uploadInventoryId,
            });
          }

          let startPage = 1;
          let startIndex = 0;
          if (existingResume && existingResume.page > 0) {
            const cursor = await findInventoryResumeCursor(
              client,
              currentIntegration.uid,
              {
                ...existingResume,
                uploadInventoryId,
              }
            );
            if (cursor.done) {
              await clearVintedInventoryResume(integrationId);
              return;
            }
            startPage = cursor.page;
            startIndex = cursor.startIndex;
          }

          await runInventoryImportPages({
            client,
            integration: currentIntegration,
            importType,
            timestamp,
            uploadInventoryId,
            batchIndex,
            startPage,
            startIndex,
            lock,
            enrichInventory: (enrichClient, items, page) =>
              enrichInventoryItemsWithPageDetails(enrichClient, items, {
                syncRunId,
                job: "inventory-import",
                importType,
                trigger: "manual",
                page,
              }),
          });

          await clearVintedInventoryResume(integrationId);
        },
        {
          ttlMs: VintedSyncLockTtlMs,
          claimedOwner,
          onLostLock: () => {
            aborted = true;
            recordVintedSyncAborted(integration, {
              job: "inventory-import",
              syncRunId,
              trigger: "manual",
              importType,
              reason: "lost-lock",
              resumed: resume || undefined,
            });
          },
        }
      );
      if (!started) {
        lockBlocked = true;
        if (claimedOwner) {
          await releaseSyncLeaseIfOwner(
            integrationId,
            runningKeys,
            claimedOwner
          );
        }
        notifySyncAlreadyLocked(dispatch, updateSyncStatus, "Vinted");
        recordVintedSyncAborted(integration, {
          job: "inventory-import",
          syncRunId,
          trigger: "manual",
          importType,
          reason: "lock-blocked",
          resumed: resume || undefined,
        });
        return { error: true };
      }
      if (!aborted) {
        recordVintedSyncFinished(integration, {
          job: "inventory-import",
          syncRunId,
          trigger: "manual",
          importType,
          durationMs: Date.now() - startedAt,
          resumed: resume || undefined,
        });
      }
    } catch (e) {
      if (claimedOwner) {
        await releaseSyncLeaseIfOwner(integrationId, runningKeys, claimedOwner);
      }
      if (dispatchNotLoggedInError(dispatch, e, "manual")) {
        preserveLoginDialog = true;
        recordVintedSyncAborted(integration, {
          job: "inventory-import",
          syncRunId,
          trigger: "manual",
          importType,
          reason: "not-logged-in",
          error: e,
          resumed: resume || undefined,
        });
      } else {
        console.error(e);
        toast.error(`Error: ${(e as Error).message}`);
        recordVintedSyncAborted(integration, {
          job: "inventory-import",
          syncRunId,
          trigger: "manual",
          importType,
          reason: "error",
          error: e,
          resumed: resume || undefined,
        });
      }
      return { error: true };
    } finally {
      if (!lockBlocked && !preserveLoginDialog)
        dispatch(updateSyncStatus(undefined));
      clearBeforeUnloadHandler("Vinted");
    }

    await updateIntegration(integrationId);
    return { success: true };
  };
}
